import { Button, Table, TableColumnsType, TableProps, Tooltip } from 'antd';
import SafeImage from 'components/SafeImage';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import useModal from 'hooks/useModal';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import ItemProblemModal from './ItemProblemModal';
import styled from '@emotion/styled';
import { What } from 'views/Aide/Scenario/PolicyTable';
import useModeTranslate from './useModeTranslate';
import { useMemo } from 'react';
import { uniq } from 'lodash-es';
import { formatNumber } from 'utils/numberFormat';
import { Index, Index2 } from './styles';

type RT = ReviewAnalysis.ItemProblem;

function ItemProblemTable({
  isVoice,
  isItemDistribution,
  isAdvantage,
  sort,
  sortOrder,
  onUpdate,
  dataSource,
  ...otherProps
}: TableProps<RT> & {
  isVoice?: boolean;
  isItemDistribution?: boolean;
  isAdvantage?: boolean;
  sort: 'REVIEW_COUNT' | 'BAD_COUNT' | 'BAD_RATE';
  sortOrder: 'DESC' | 'ASC';
  onUpdate: (record: RT) => void;
}) {
  const texts = useMemo(
    () =>
      uniq(
        dataSource?.reduce<string[]>((res, { problemList }) => {
          return problemList.slice(0, 3).reduce((res, { content }) => {
            if (content) {
              res.push(content);
            }
            return res;
          }, res);
        }, []) || [],
      ),
    [dataSource],
  );

  const { t, trigger: translateTrigger, result: translateResult } = useModeTranslate(texts);
  const getRegionLabel = useGetRegionLabel();

  const { visible, show, hide, update, meta } = useModal<RT>();

  const modal = meta && (
    <ItemProblemModal
      visible={visible}
      onCancel={() => hide()}
      isVoice={isVoice}
      isItemDistribution={isItemDistribution}
      isAdvantage={isAdvantage}
      itemProblem={meta}
      onUpdate={(list) => {
        const next = {
          ...meta,
          problemList: list,
        };
        update(visible, next);
        onUpdate(next);
      }}
    />
  );

  const { config } = useStoreNameConfig();

  const columns: TableColumnsType<RT> = [
    {
      key: 'itemData',
      dataIndex: 'itemData',
      title: t('商品'),
      width: 33,
      ellipsis: true,
      render: ({ img, name, platform, region, storeName, storeAlias }: RT['itemData']) => {
        return (
          <Item>
            <SafeImage width={56} height={56} src={img} />
            <section>
              <p>{name}</p>
              <div>
                {platformLabelMap[toLowerCase(platform)]}-{getRegionLabel(region)}-
                {isStoreAliasFirst(config, platform, region) ? storeAlias || storeName : storeName}
              </div>
            </section>
          </Item>
        );
      },
    },
    {
      key: 'problemList',
      dataIndex: 'problemList',
      title: (
        <div className="flex items-center justify-between flex-wrap">
          <div>{isAdvantage ? t('优点TOP3') : t('问题TOP3')}</div>
          {texts.length > 0 && <div>{translateTrigger}</div>}
        </div>
      ),
      width: 24,
      render: (problemList: RT['problemList'], record) => {
        return (
          <div className="my-[-12px]">
            {problemList.slice(0, 3).map(({ content }, index) => (
              <div className="flex items-center flex-wrap text-wrap">
                {isAdvantage ? (
                  <Index2 index={String(index)}>
                    <span>{index + 1}</span>
                  </Index2>
                ) : (
                  <Index index={String(index)}>{index + 1}</Index>
                )}
                {content}
                {translateResult?.[content] && (
                  <span className="ml-3">{translateResult[content]}</span>
                )}
              </div>
            ))}
            <Button size="small" type="link" onClick={() => show(record)}>
              {t('filter.all')}（{problemList.length}）
            </Button>
          </div>
        );
      },
    },
    ...(isVoice
      ? [
          {
            key: 'reviewCount',
            dataIndex: 'reviewCount',
            title: (
              <div>
                {t('评价数')}
                <Tooltip title={isAdvantage ? t('评价数tips4') : t('评价数tips3')}>
                  <What />
                </Tooltip>
              </div>
            ),
            width: 13,
            sorter: true,
            showSorterTooltip: false,
            sortOrder:
              sort === 'REVIEW_COUNT'
                ? (
                    {
                      DESC: 'descend',
                      ASC: 'ascend',
                    } as const
                  )[sortOrder]
                : null,
          },
        ]
      : []),
    {
      key: 'negativeReviewCount',
      dataIndex: 'negativeReviewCount',
      title: (
        <div>
          {isItemDistribution ? t('问题差评数') : isAdvantage ? t('好评数') : t('差评数')}
          <Tooltip
            title={
              isAdvantage
                ? t('好评数tips')
                : t(`差评数tips${isVoice ? '3' : isItemDistribution ? '2' : '1'}`)
            }
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 13,
      sorter: true,
      showSorterTooltip: false,
      sortOrder:
        sort === 'BAD_COUNT'
          ? (
              {
                DESC: 'descend',
                ASC: 'ascend',
              } as const
            )[sortOrder]
          : null,
    },
    {
      key: 'negativeReviewPercent',
      dataIndex: 'negativeReviewPercent',
      title: (
        <div>
          {isItemDistribution ? t('问题差评率') : isAdvantage ? t('好评率') : t('差评率')}
          <Tooltip
            title={
              isAdvantage
                ? t('好评率tips')
                : t(`差评率tips${isVoice ? '3' : isItemDistribution ? '2' : '1'}`)
            }
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 13,
      sorter: true,
      showSorterTooltip: false,
      sortOrder:
        sort === 'BAD_RATE'
          ? (
              {
                DESC: 'descend',
                ASC: 'ascend',
              } as const
            )[sortOrder]
          : null,
      render: (percent: RT['negativeReviewPercent']) =>
        formatNumber(Number(percent || 0) / 100, '0.00%'),
    },
    {
      key: 'problemCount',
      dataIndex: 'problemCount',
      title: (
        <div>
          {isAdvantage ? t('优点数') : t('问题数')}
          <Tooltip
            title={
              isAdvantage
                ? t('优点数tips')
                : t(`问题数tips${isVoice ? '3' : isItemDistribution ? '2' : '1'}`)
            }
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 11,
    },
  ];

  const table = (
    <Table
      rowKey={({ itemData }) => itemData.id}
      columns={columns}
      dataSource={dataSource}
      {...otherProps}
    />
  );

  return (
    <>
      {table}
      {modal}
    </>
  );
}

const Item = styled.div`
  display: flex;
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    > div {
      font-size: 12px;
      color: var(--fett-text-tertiary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export default ItemProblemTable;
